import { makeStyles } from '@fluentui/react-components';
import { shellMedia, shellMetrics, tokens } from '@vigeo/ui';

/**
 * The person record's shared styles, MOVED here unchanged from `pages/PersonPage.tsx` (#462 §5).
 *
 * One definition, not one per step file. The steps are owned by two teams now, and a copy each
 * is how the record's cards and disclosures start looking subtly different depending on which
 * team last touched them.
 */
export const usePersonStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, minWidth: 0 },
  head: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  headline: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  name: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightBold },
  meta: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  /**
   * The badge itself is the control: a button that adds nothing visual but the affordance.
   *
   * ON A PHONE IT IS ALSO A TAP TARGET. The badge renders about 20px tall, and
   * `e2e/person-sections.spec.ts` requires every interactive element to be at least 44px on its
   * short side at 390px — it caught this, eleven times, once per section. The minimum is scoped to
   * `shellMedia.phone` so the desktop header keeps the height frame 2.6 draws; the same pattern
   * and the same token are what `PersonCard` and the shell's own nav already use.
   */
  stageButton: {
    background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', color: 'inherit',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
    borderRadius: tokens.borderRadiusMedium,
    ':focus-visible': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`, outlineOffset: tokens.spacingHorizontalXXS },
    /**
     * The 44px is the HIT AREA, not the layout height: the negative margin gives it back to the
     * row so the sticky record header does not grow. It matters at 320px, where growing it left
     * the scrollable content 84px tall and `person-sections.spec.ts` refuses anything under 100.
     * The badge still looks exactly as frame 2.6 draws it; only the tappable box is bigger.
     */
    [shellMedia.phone]: {
      minHeight: shellMetrics.touchTarget,
      marginBlock: `calc((${shellMetrics.touchTarget} - ${tokens.lineHeightBase300}) / -2)`,
    },
  },
  stagePanel: {
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingVerticalM, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM,
    minWidth: 0, maxWidth: '32rem',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusLarge,
    paddingBlock: tokens.spacingVerticalM, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0,
  },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  h: { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold, margin: 0 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  /**
   * A LABELLED ROW: the label and its explanation on the left, the content on the right, divided
   * from the row above. Frames `278:6492` and `277:6308` both draw their sections this way rather
   * than as a stack of headed cards.
   *
   * It collapses to one column below tablet, where a 13rem label column would leave the content
   * about 7rem wide on a phone.
   */
  labelledRow: {
    display: 'grid', gridTemplateColumns: '13rem minmax(0, 1fr)', gap: tokens.spacingHorizontalL,
    alignItems: 'start', minWidth: 0,
    paddingBlock: tokens.spacingVerticalL,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    [shellMedia.belowTablet]: { gridTemplateColumns: 'minmax(0, 1fr)', gap: tokens.spacingVerticalS },
  },
  rowLabel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  rowBody: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, minWidth: 0, alignItems: 'flex-start' },
  /**
   * A LIST OF RECORDS, not a bulleted list. The frames draw each submission and each portal step
   * as a row — name and detail stacked on the left, status and the one action on the right — with
   * no marker. Left as a `ul`/`ol` for the semantics; only the marker and the inline flow go.
   */
  recordList: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 },
  recordRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap', minWidth: 0, paddingBlock: tokens.spacingVerticalS,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    ':first-of-type': { borderTopStyle: 'none' },
  },
  /**
   * The same row list, numbered — frame `278:6492` draws "What <first> sees" as 1 to 5.
   *
   * A CSS COUNTER on a real `ol`, not numbers rendered into the text: the list keeps its meaning
   * for a screen reader, and the printed number cannot drift from the row's actual position, which
   * is the whole point of a numbered order the candidate also sees.
   */
  numberedList: { counterReset: 'portal-step' },
  /**
   * The filter radios WRAP, and are tappable.
   *
   * Fluent's horizontal `RadioGroup` is a flex row that does not wrap, so four filters with their
   * counts ran off the side of a 320px screen. And its `__input` renders 32x32, which
   * `person-sections.spec.ts` refuses at 390px — every interactive element must be at least 44 on
   * its short side. The minimum goes on the input itself, which is the box the probe measures;
   * the radios sit far enough apart that widening it cannot overlap its neighbour.
   */
  filterGroup: {
    flexWrap: 'wrap', rowGap: tokens.spacingVerticalXS,
    [shellMedia.phone]: {
      '& .fui-Radio': { minHeight: shellMetrics.touchTarget, alignItems: 'center' },
      '& .fui-Radio__input': { minHeight: shellMetrics.touchTarget, minWidth: shellMetrics.touchTarget },
    },
  },
  numberedRow: {
    counterIncrement: 'portal-step',
    '::before': {
      content: 'counter(portal-step) ". "',
      color: tokens.colorNeutralForeground3,
      marginInlineEnd: tokens.spacingHorizontalXS,
    },
  },
  /*
   * `1 1 8rem`, not 16rem. A 16rem basis is 256px, which at the 320px breakpoint left the row
   * wider than the section and Browser QA caught it at 320 and 390 — `scrollWidth 359 > 289`.
   * `overflowWrap` matters for the same reason: a long form name is one unbreakable token.
   */
  recordMain: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    minWidth: 0, flex: '1 1 8rem', overflowWrap: 'anywhere',
  },
  recordAside: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  centered: { display: 'flex', justifyContent: 'center', paddingBlock: tokens.spacingVerticalXXXL },
  disclosure: { paddingBlock: tokens.spacingVerticalM, borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
  summary: { cursor: 'pointer', paddingBlock: tokens.spacingVerticalM, fontWeight: tokens.fontWeightSemibold },
});
